import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import connect from 'react-redux/es/connect/connect';
import withReactContent from 'sweetalert2-react-content';
import swal from 'sweetalert2';
import { toast } from 'react-toastify';

import { getDefaultStyles } from '../../../../helpers/getDefaultStyles';
import {
  getPromoterStyleTheme,
  updatePromoterStyleTheme,
} from '../../../../store/actions/promoterActions';
import NewLoader from '../../../../components/NewLoader/NewLoader';
import axios from '../../../../axios-instance';

import './ThemesPage.sass';
import { ThemeSettings } from '../../../../components/Themes/ThemeSettings/ThemeSettings';
import { getHeaders } from '../../../../helpers/getHeaders';
import { ThemeFieldSettings } from '../../../../components/Themes/ThemeSettings/ThemeFieldSettings';
import { AddFieldModal } from '../../../../components/Themes/Modals/AddFieldModal';
import { allowedSigns, wrongSigns } from '../../../../components/Themes/constants';

const MySwal = withReactContent(swal);

export const PromoterThemesPage = ({
  token,
  loading,
  getPromoterStyleTheme,
  updatePromoterStyleTheme,
}) => {
  const {
    fontFamily,
    primaryColor,
    primarySemiLightColor,
    primaryLightColor,
    secondaryColor,
    secondaryLightColor,
    backgroundColor,
    textColor,
    secondaryTextColor,
  } = getDefaultStyles(document.getElementById('body'));

  const [styleTheme, setStyleTheme] = useState({
    fontFamily,
    primaryColor,
    primarySemiLightColor,
    primaryLightColor,
    secondaryColor,
    secondaryLightColor,
    backgroundColor,
    primaryTextColor: textColor,
    secondaryTextColor,
  });
  const [fieldTheme, setFieldTheme] = useState([]);

  const [isStyleThemeEdited, setStyleThemeEdited] = useState(false);
  const [isFieldsLoading, setFieldsLoading] = useState(true);

  const getPromoterFieldTheme = async () => {
    setFieldsLoading(true);

    return axios
      .get(`/TransactionFields/Promoter`, { headers: getHeaders(token) })
      .then((response) => {
        if (response.data.isSuccess) {
          return response.data.result;
        }
        throw new Error(response.data.errorMessage);
      })
      .catch((err) => {
        toast.error('Fields theme not received');
      })
      .finally(() => {
        setFieldsLoading(false);
      });
  };

  const getData = async () => {
    try {
      const [fields, styles] = await Promise.all([
        getPromoterFieldTheme(),
        getPromoterStyleTheme(token),
      ]);

      setFieldTheme(fields);

      if (styles) {
        setStyleTheme(styles);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleChangeFieldSetting = (type, key, value, id) => {
    const oldData = fieldTheme.find((ft) => ft.id === id);
    const newData = {
      ...oldData,
      [key]: value,
    };

    try {
      axios
        .put(`/TransactionFields/Promoter`, newData, {
          headers: getHeaders(token),
        })
        .then((response) => {
          if (response.data.isSuccess) {
            setFieldTheme((prev) => {
              return prev.map((f) => {
                if (f.id !== id) return f;

                return {
                  ...f,
                  [key]: value,
                };
              });
            });

            return response.data.result;
          }
          throw new Error(response.data.errorMessage);
        })
        .catch((err) => {
          toast.error('Fields theme not saved');
        });
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const handleChangeCompanyData = async (key, value, id) => {
    // type 9 || 10
    try {
      await Promise.all([
        handleChangeFieldSetting(9, key, value, id),
        handleChangeFieldSetting(10, key, value, id),
      ]);
    } catch (error) {
      toast.error('Fields theme not saved');
    }
  };

  const handleAddFieldSubmit = (data) => {
    setFieldsLoading(true);

    try {
      axios
        .post(`/TransactionFields/Promoter`, data, {
          headers: getHeaders(token),
        })
        .then((response) => {
          if (response.data.isSuccess) {
            toast.success('Field is added successfully');
            MySwal.close();

            getData();

            return response.data.result;
          }
          throw new Error(response.data.errorMessage);
        })
        .catch((err) => {
          toast.error('Fields theme not added');
          setFieldsLoading(false);
        });
    } catch (error) {
      toast.error('Something went wrong');
    }
  }

  const addField = () => {
    MySwal.fire({
      html: <AddFieldModal handleSubmit={handleAddFieldSubmit} />,
      showConfirmButton: false,
      width: 'max-content',
    });
  };

  const removeField = async (field) => {
    setFieldsLoading(true);

    return axios
      .delete(`/TransactionFields/Promoter/${field.id}`, {
        headers: getHeaders(token),
      })
      .then((response) => {
        if (response.data.isSuccess) {
          toast.success('Field is removed successfully');

          getData();

          return response.data.result;
        }
        throw new Error(response.data.errorMessage);
      })
      .catch((err) => {
        toast.error('Fields theme not received');
        setFieldsLoading(false);
      });
  }

  const handleChangeStyleSetting = (name, value) => {
    setStyleThemeEdited(true);
    setStyleTheme((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveStyleTheme = () => {
    MySwal.fire({
      title: 'Do you want to save changes?',
      customClass: 'swal2-modal',
      showCancelButton: true,
      confirmButtonText: 'Save',
      reverseButtons: true,
    }).then((result) => {
      if (result.value) {
        updatePromoterStyleTheme(token, styleTheme).then(() =>
          setStyleThemeEdited(false)
        );
      }
    });
  };

  useEffect(() => {
    getData();
  }, []);

  if (loading || isFieldsLoading) {
    return <NewLoader />;
  }

  return (
    <section className='promoter-theme'>
      <div className='promoter-theme__settings-container'>
        <div>
          <ThemeFieldSettings
            fieldTheme={fieldTheme}
            handleChangeCompanyData={handleChangeCompanyData}
            handleChangeFieldSetting={handleChangeFieldSetting}
            removeField={removeField}
          />
          <button
            type='button'
            className='btn-primary promoter-theme__btn'
            onClick={addField}
          >
            Add field
          </button>
        </div>

        <div>
          <ThemeSettings
            theme={styleTheme}
            handleChangeSetting={handleChangeStyleSetting}
          />
          <button
            type='button'
            className='btn-primary promoter-theme__btn'
            onClick={saveStyleTheme}
            disabled={!isStyleThemeEdited}
          >
            Save colors
          </button>
        </div>
      </div>
    </section>
  );
};

const mapStateToProps = ({ auth, promoter }) => ({
  token: auth.user.token,
  loading: promoter.loading,
  theme: promoter.theme,
});

const mapDispatchToProps = (dispatch) => ({
  getPromoterStyleTheme: (token) => dispatch(getPromoterStyleTheme(token)),
  updatePromoterStyleTheme: (token, theme) =>
    dispatch(updatePromoterStyleTheme(token, theme)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PromoterThemesPage)
);
